import { useState, useEffect, useCallback, useRef } from 'react';
import examSessionService from '../services/examSessionService';
import { Section, Question } from '../types';

interface UseExamSessionProps {
  assignmentId: string;
  candidateId: string;
  sections: Section[];
  questions: Question[];
  totalDuration: number;
}

export const useExamSession = ({
  assignmentId,
  candidateId,
  sections,
  questions,
  totalDuration
}: UseExamSessionProps) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [timeLeft, setTimeLeft] = useState(totalDuration * 60);
  const [examStarted, setExamStarted] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [sectionTimes, setSectionTimes] = useState<Record<string, number>>({});
  
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  useEffect(() => {
    initializeSession();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, []);
  
  const initializeSession = async () => {
    try {
      setLoading(true);
      const existingSessionId = localStorage.getItem(`exam_session_${assignmentId}`);
      
      if (existingSessionId) {
        const session = await examSessionService.getSession(existingSessionId);
        if (session && session.status !== 'submitted') {
          setSessionId(existingSessionId);
          setCurrentSectionIndex(session.currentSectionIndex);
          setCurrentQuestionIndex(session.currentQuestionIndex);
          setAnswers(session.answers);
          setExamStarted(session.status === 'in_progress');
          
          const elapsedTime = session.timeSpent;
          setTimeLeft(Math.max(0, totalDuration * 60 - elapsedTime));
        } else {
          await createNewSession();
        }
      } else {
        await createNewSession();
      }
    } catch (error) {
      console.error("Error initializing session:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const createNewSession = async () => {
    const newSessionId = await examSessionService.createSession(assignmentId, candidateId);
    setSessionId(newSessionId);
    localStorage.setItem(`exam_session_${assignmentId}`, newSessionId);
  };
  
  const startExam = async () => {
    if (!sessionId) return;
    
    await examSessionService.startExam(sessionId);
    setExamStarted(true);
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          submitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    autoSaveRef.current = setInterval(() => {
      if (sessionId && Object.keys(answers).length > 0) {
        examSessionService.autoSaveProgress(sessionId, answers);
      }
    }, 30000);
  };
  
  const saveAnswer = useCallback(async (questionId: string, answer: any, sectionId: string) => {
    if (!sessionId) return;
    
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    await examSessionService.saveAnswer(sessionId, questionId, answer, sectionId, 0);
  }, [sessionId]);
  
  const updateProgress = useCallback(async () => {
    if (!sessionId) return;
    
    const elapsedTime = totalDuration * 60 - timeLeft;
    await examSessionService.updateSessionProgress(
      sessionId,
      currentSectionIndex,
      currentQuestionIndex,
      elapsedTime
    );
  }, [sessionId, currentSectionIndex, currentQuestionIndex, timeLeft, totalDuration]);
  
  const updateSectionStatus = useCallback(async (sectionId: string, status: 'pending' | 'in_progress' | 'completed') => {
    if (!sessionId) return;
    
    await examSessionService.updateSectionStatus(sessionId, sectionId, status, sectionTimes[sectionId]);
  }, [sessionId, sectionTimes]);
  
  const submitExam = async () => {
    if (!sessionId || examSubmitted) return;
    
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    
    await examSessionService.submitExam(sessionId, assignmentId, candidateId);
    setExamSubmitted(true);
    
    localStorage.removeItem(`exam_session_${assignmentId}`);
  };
  
  const goToNextQuestion = () => {
    const currentSectionQuestions = questions.filter(q => q.sectionId === sections[currentSectionIndex]?.id);
    
    if (currentQuestionIndex < currentSectionQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setCurrentQuestionIndex(0);
    }
    
    updateProgress();
  };
  
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      const prevSectionQuestions = questions.filter(q => q.sectionId === sections[currentSectionIndex - 1]?.id);
      setCurrentQuestionIndex(prevSectionQuestions.length - 1);
    }
    
    updateProgress();
  };
  
  const jumpToQuestion = (sectionIdx: number, questionIdx: number) => {
    setCurrentSectionIndex(sectionIdx);
    setCurrentQuestionIndex(questionIdx);
    updateProgress();
  };
  
  const getAnsweredCount = useCallback(() => {
    return Object.keys(answers).length;
  }, [answers]);
  
  const getSectionProgress = useCallback((sectionId: string) => {
    const sectionQuestions = questions.filter(q => q.sectionId === sectionId);
    const answeredInSection = sectionQuestions.filter(q => answers[q.id]).length;
    return {
      answered: answeredInSection,
      total: sectionQuestions.length,
      percentage: (answeredInSection / sectionQuestions.length) * 100
    };
  }, [questions, answers]);
  
  return {
    loading,
    sessionId,
    currentSectionIndex,
    currentQuestionIndex,
    answers,
    timeLeft,
    examStarted,
    examSubmitted,
    startExam,
    saveAnswer,
    submitExam,
    goToNextQuestion,
    goToPreviousQuestion,
    jumpToQuestion,
    getAnsweredCount,
    getSectionProgress,
    updateSectionStatus
  };
};